import { useQuery } from '@apollo/react-hooks';
import moment from 'moment';
import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { AppContext } from '../../../context/AppContext';
import { GET_CAMPAIGN } from '../../../graphql/queries';
import colors from '../../../styles/colors';
import Button from '../../Button';
import Loader from '../../Loader';
import { getDateByOffset } from '../../../util/Time';
import { useMobileMedia } from '../../../util/MediaQuery';
import analyticsKeys from '../../../util/UsageStatLinks';
import useMutateAnalytics from '../../../util/mutate.Analytics';
import { CampaignModal } from '../../../util/Campaign';

const Container = styled.ul`
  width: 30vw;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.05);
  margin: 0 10px;
  list-style: none;
  /* padding: 0 15px; */
  overflow: auto;
  position: relative;
  @media screen and (max-width: 991px) {
    width: 50vw;
  }

  @media screen and (max-width: 450px) {
    width: calc(75vw - 20px);
    max-height: 70vh;
  }
  &::-webkit-scrollbar {
    width: 5px;
    height: 5px;
  }
  &::-webkit-scrollbar-track {
    box-shadow: inset 0 0 5px grey;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: grey;
    border-radius: 10px;
  }
`;

const ListHeader = styled.h2`
  min-height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-bottom: 1px solid ${colors.grey4};
  padding: 10px 0;
`;

const ListItem = styled.li`
  min-height: 30px;
  text-align: justify;
  border-bottom: 1px solid ${colors.grey5};
  padding: 10px;
  margin-bottom: 5x;
  font-size: 13px;
  display: flex;
  flex-direction: row;
  height: auto;
`;

const ListKey = styled.div`
  min-width: 100px;
  font-weight: 450;
  text-align: left;
`;

const ListValue = styled.div`
  display: flex;
  flex: 1;
  color: ${colors.grey2};
  word-break: break-word;
  text-align: left;
`;

export const ImagePreview = styled.img`
  max-width: 100%;
  height: 180px;
  border-radius: 7px;
  opacity: ${(props) => props.opacity};
  @media (max-width: 450px) {
    height: auto;
  }
`;

const ButtonContainer = styled.div`
  padding: 20px 0px;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  border-radius: 7px;
  min-height: 40px;
  position: sticky;
  bottom: 0;
  background-color: ${colors.white};
  z-index: 50;
`;

const LoaderContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
`;

const UrlsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const Url = styled.div`
  display: inline-block;
  width: 80%;
  word-break: break-word;
  padding: 10px 0;
`;

const ClicksCount = styled.div`
  display: inline-flex;
  width: 20%;
  justify-content: center;
  padding: 10px 0;
`;

const executionStatus = Object.freeze({
  Pending: 'Pending',
  Processing: 'Processing',
  Completed: 'Completed',
  Failed: 'Failed',
});

const CampaignDetailsModal = ({ campaignId }) => {
  const { data: campaignData, loading } = useQuery(GET_CAMPAIGN, {
    variables: {
      campaignId,
    },
  });
  const { app } = useContext(AppContext);
  const history = useHistory();
  const onEditClick = () => {
    const mutateAnalyticsVariables = {
      link: analyticsKeys.CONTACTS_SEND_WELCOMES,
    };
    mutateAnalytics(mutateAnalyticsVariables);

    const pathType = campaignData?.campaign?.isTriggerCampaign
      ? CampaignModal.TriggerCampaign
      : CampaignModal.GenCampagian;

    history.push({
      pathname: `/campaigns/id=${campaignData.campaign._id}`,
      search: `?type=${pathType}`,
      state: { campaignType: pathType },
    });
  };
  const onCopyClick = () => {
    const mutateAnalyticsVariables = {
      link: analyticsKeys.CONTACTS_SEND_WELCOMES,
    };
    mutateAnalytics(mutateAnalyticsVariables);
    const pathType = campaignData?.campaign?.isTriggerCampaign
      ? CampaignModal.TriggerCampaign
      : CampaignModal.GenCampagian;
    history.push({
      pathname: '/campaigns/new',
      search: `?type=${pathType}&id=${campaignData.campaign._id}`,
      state: { campaignType: pathType },
    });
  };

  const date = getDateByOffset(
    app.orgTimeZoneOffset,
    new Date(campaignData && campaignData.campaign.dateTime * 1000),
  );

  const format = 'MM/DD/YYYY hh:mm A';
  const formattedDate = moment(date).format(format);
  const isMobile = useMobileMedia();
  const mutateAnalytics = useMutateAnalytics();

  return (
    <Container>
      {loading ? (
        <LoaderContainer>
          <Loader />
        </LoaderContainer>
      ) : (
        <>
          {campaignData?.campaign && (
            <>
              <ListHeader>{campaignData.campaign.name}</ListHeader>
              {isMobile && (
                <ListItem>
                  <ListKey>Status :</ListKey>
                  <ListValue>
                    {campaignData.campaign.executionStatus
                    === executionStatus.Processing
                      ? 'In Progress'
                      : campaignData.campaign.status}
                  </ListValue>
                </ListItem>
              )}
              <ListItem>
                <ListKey>Date-Time :</ListKey>
                <ListValue> {formattedDate}</ListValue>
              </ListItem>
              {campaignData.campaign.imageUrl && (
                <ListItem>
                  <ListKey>Image :</ListKey>
                  <ListValue>
                    {' '}
                    <ImagePreview
                      src={campaignData.campaign.imageUrl}
                      style={{ zIndex: '10' }}
                    />
                  </ListValue>
                </ListItem>
              )}
              <ListItem>
                <ListKey>Message :</ListKey>
                <ListValue>
                  {campaignData.campaign.text}
                  {campaignData.campaign.referralOptInText && (
                    <>
                      <br />
                      {campaignData.campaign.referralOptInText}
                    </>
                  )}
                  {
                    <>
                      <br />
                      {campaignData.campaign.unsubscribeMessage}
                    </>
                  }
                </ListValue>
              </ListItem>
              <ListItem>
                <ListKey>Tags :</ListKey>
                <ListValue>
                  {Array.isArray(campaignData.campaign.tags) && campaignData.campaign.tags.length > 0
                    ? campaignData.campaign.tags.join(', ')
                    : ''}
                </ListValue>
              </ListItem>
              <ListItem>
                <ListKey>Number Sent :</ListKey>
                <ListValue> {campaignData.campaign?.numberSent}</ListValue>
              </ListItem>

              {campaignData.campaign?.shortUrls?.length > 0 && (
                <ListItem>
                  <ListKey>Click-Thrus / Conversions :</ListKey>
                  <UrlsContainer>
                    {campaignData.campaign?.shortUrls.map((item) => {
                      return (
                        <div key={item._id}>
                          <Url>{item.originalUrl}</Url>
                          <ClicksCount>{item.clicks}</ClicksCount>
                        </div>
                      );
                    })}
                  </UrlsContainer>
                </ListItem>
              )}

              <ListItem>
                <ListKey>Credits Used :</ListKey>
                <ListValue> {campaignData.campaign.creditsUsed}</ListValue>
              </ListItem>
              <ListItem>
                <ListKey>Opt Outs :</ListKey>
                <ListValue> {campaignData.campaign.optOuts}</ListValue>
              </ListItem>
              <ButtonContainer>
                {campaignData.campaign.executionStatus
                  === executionStatus.Pending && (
                  <Button
                    text="Edit"
                    onClick={() => onEditClick()}
                    width="90px"
                    type="button"
                    padding="0px"
                    height="35px"
                  />
                )}
                <Button
                  text="Replicate"
                  onClick={() => onCopyClick()}
                  padding="0px"
                  width="90px"
                  height="35px"
                />
              </ButtonContainer>
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default CampaignDetailsModal;
